import { useEffect, type RefObject } from "react";

/**
 * Detect mousedown/touchstart outside a referenced element and invoke handler.
 * Used for dropdowns, popovers, and click-away dismissal.
 *
 * @param ref Reference to the element whose outside clicks should be detected
 * @param handler Callback fired when click happens outside the ref's tree
 * @param enabled When false, no listener is attached (default: true)
 */
export function useClickOutside<T extends HTMLElement>(
  ref: RefObject<T | null>,
  handler: (event: MouseEvent | TouchEvent) => void,
  enabled = true,
): void {
  useEffect(() => {
    if (!enabled) return;
    const onEvent = (e: MouseEvent | TouchEvent) => {
      const el = ref.current;
      if (!el) return;
      const target = e.target as Node | null;
      if (target && el.contains(target)) return;
      handler(e);
    };
    document.addEventListener("mousedown", onEvent);
    document.addEventListener("touchstart", onEvent);
    return () => {
      document.removeEventListener("mousedown", onEvent);
      document.removeEventListener("touchstart", onEvent);
    };
  }, [ref, handler, enabled]);
}
